<template>
    <div class="workboard">
        <div class="workboard__container">
            <transition name="transitions__slide-from-right" mode="out-in" appear>
                <div v-if="!isFetchingData" :key="showReferralsReceived" class="workboard__statistics">
                    <workboard-status
                        :numberOfReferrals="pendingCount"
                        :title="$t('pages.index.home.workboard.pending')"
                        :subtitle="pendingAppointmentSubtitle"
                        :hint="$t('pages.index.home.workboard.appointment_hint')"
                        icon="time"
                        iconSize="extra-huge"
                        @go-to-referrals="goToCreatedReferrals()"
                    ></workboard-status>
                    <workboard-status
                        :numberOfReferrals="insuranceAuthPendingCount"
                        :title="$t('pages.index.home.workboard.pending')"
                        :subtitle="$t('pages.index.home.workboard.insurance_auth')"
                        :hint="$t('pages.index.home.workboard.insurance_auth_hint')"
                        iconSize="extra-huge"
                        icon="insurance-dark"
                        @go-to-referrals="goToInsuranceAuthPendingReferrals()"
                    ></workboard-status>
                    <workboard-status
                        :numberOfReferrals="patientNoShowCount"
                        :title="$t('pages.index.home.workboard.no')"
                        :subtitle="$t('pages.index.home.workboard.show')"
                        :hint="$t('pages.index.home.workboard.no_show_hint')"
                        iconSize="extra-huge"
                        icon="member-dark"
                        @go-to-referrals="goToNoShowReferrals()"
                    ></workboard-status>
                    <workboard-status
                        :numberOfReferrals="consultationNotesPendingCount"
                        :title="$t('pages.index.home.workboard.pending')"
                        :subtitle="$t('pages.index.home.workboard.consultation_notes')"
                        :hint="$t('pages.index.home.workboard.consultation_notes_hint')"
                        iconSize="extra-huge"
                        icon="cloud-dark"
                        @go-to-referrals="goToConsultationNotesPendingReferrals()"
                    ></workboard-status>
                </div>
            </transition>
        </div>

        <div v-if="!$auth.isUserPracticeOther()" class="workboard__switch" @click="switchReferrals">
            <common-icon class="workboard-status__icon" :name="switchIcon" size="extra-huge"></common-icon>
            <span>{{ switchTitle }}</span>
        </div>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import utc from 'dayjs/plugin/utc'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import WorkboardStatus from 'components/page-elements/index/workboard/WorkboardStatus'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralService from 'services/ReferralService'

    dayjs.extend(utc)

    export default {
        components: { WorkboardStatus },

        data() {
            return {
                practice: this.$auth.getPractice(),
                user: this.$auth.getActiveMember(),
                isFetchingData: false,
                showReferralsReceived: this.$auth.isUserPracticeOther() ? false : true,
                referralsCount: { made: [], received: [] },
                windowWidth: window.innerWidth,
                PracticeTypeEnum: PracticeTypeEnum,
            }
        },

        computed: {
            switchTitle() {
                return this.showReferralsReceived
                    ? this.$t('pages.index.home.workboard.switch_referrals_made')
                    : this.$t('pages.index.home.workboard.switch_referrals_received')
            },

            switchIcon() {
                return this.showReferralsReceived ? 'switch-made' : 'switch-received'
            },

            pendingCount() {
                if (this.$auth.isUserPracticeDemo()) {
                    return 1
                }
                const statuses = this.showReferralsReceived
                    ? [ReferralStatusEnum.REFERRALS_POOL_RECEIVED, ReferralStatusEnum.REFERRALS_POOL_RECEIVED_BY_PATIENT]
                    : [ReferralStatusEnum.REFERRALS_POOL_SLOTS_PROPOSED]

                return this.referralsCount[this.showReferralsReceived ? 'received' : 'made']
                    ?.filter((r) => statuses.includes(r.referralStatus))
                    .reduce((acc, val) => acc + this.getCount(val), 0)
            },

            insuranceAuthPendingCount() {
                const insuranceAuthPendingReferrals = this.referralsCount[this.showReferralsReceived ? 'received' : 'made']?.find(
                    (r) => r.referralStatus === ReferralStatusEnum.INSURANCE_AUTH_PENDING,
                )
                return this.getCount(insuranceAuthPendingReferrals)
            },

            patientVisitPastCount() {
                const patientVisitPastReferrals = this.referralsCount[this.showReferralsReceived ? 'received' : 'made']?.find(
                    (r) => r.referralStatus === 'PATIENT_VISIT_PAST',
                )
                return this.getCount(patientVisitPastReferrals)
            },
            patientNoShowCount() {
                const patientNoShowReferrals = this.referralsCount[this.showReferralsReceived ? 'received' : 'made']?.find(
                    (r) => r.referralStatus === 'NO_SHOW',
                )
                return this.getCount(patientNoShowReferrals)
            },

            consultationNotesPendingCount() {
                const consultationNotesPendingReferrals = this.referralsCount[
                    this.showReferralsReceived ? 'received' : 'made'
                ]?.find((r) => r.referralStatus === ReferralStatusEnum.CONSULTATION_NOTES_PENDING)
                return this.getCount(consultationNotesPendingReferrals)
            },

            pendingAppointmentSubtitle() {
                return this.showReferralsReceived
                    ? this.$t('pages.index.home.workboard.appointment_proposals')
                    : this.$t('pages.index.home.workboard.proposal_acceptance')
            },
        },

        methods: {
            goToReferrals(query) {
                const referralsType = this.showReferralsReceived ? 'received' : 'made'
                this.$router.push({ path: `/referrals/${referralsType}`, query })
            },

            goToReferralsPool() {
                const referralsType = this.showReferralsReceived ? 'received' : 'made'
                this.$router.push({ name: `referrals.${referralsType}.pool` })
            },

            goToPendingReferrals() {
                const query = {
                    status: [
                        ReferralStatusEnum.CREATED,
                        ReferralStatusEnum.RESCHEDULE_REQUESTED,
                        ReferralStatusEnum.RESCHEDULE_AVAILABILITY_CHANGE,
                    ].join(','),
                }
                this.goToReferrals(query)
            },

            goToInsuranceAuthPendingReferrals() {
                const query = { status: ReferralStatusEnum.INSURANCE_AUTH_PENDING }
                this.goToReferrals(query)
            },

            goToPatientVisitPastReferrals() {
                const dateYesterdayUTC = dayjs().utc().subtract(1, 'day').format('YYYY-MM-DD HH:mm:ss')
                const query = {
                    status: [ReferralStatusEnum.PATIENT_VISIT_PENDING, ReferralStatusEnum.INSURANCE_AUTH_PENDING].join(','),
                    toDate: dateYesterdayUTC,
                }
                this.goToReferrals(query)
            },

            goToNoShowReferrals() {
                const query = { status: ReferralStatusEnum.NO_SHOW }
                this.goToReferrals(query)
            },

            goToCreatedReferrals() {
                const query = {
                    status: this.showReferralsReceived ? ReferralStatusEnum.CREATED : ReferralStatusEnum.TIME_SLOTS_PROPOSED,
                }
                this.goToReferrals(query)
            },

            goToConsultationNotesPendingReferrals() {
                const query = { status: ReferralStatusEnum.CONSULTATION_NOTES_PENDING }
                this.goToReferrals(query)
            },

            switchReferrals() {
                this.showReferralsReceived = !this.showReferralsReceived
                if (!this.referralsCount[this.showReferralsReceived ? 'received' : 'made'].length) {
                    this.fetchReceivedReferralsCountByStatus()
                    this.fetchMadeReferralsCountByStatus()
                }
            },

            getCount(referralCount) {
                return referralCount && referralCount.count ? referralCount.count : 0
            },

            fetchReferralsCountByStatus(params, type) {
                this.isFetchingData = true

                ReferralService.fetchReferralsCountByStatus(params).then((res) => {
                    this.referralsCount[type] = res
                    this.isFetchingData = false
                })
            },

            fetchReceivedReferralsCountByStatus() {
                const params = {}
                if (this.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                    params.toPracticeId = this.practice.id
                } else {
                    params.toUserId = this.user?.id
                }

                this.isFetchingData = true
                this.fetchReferralsCountByStatus(params, 'received')
            },

            fetchMadeReferralsCountByStatus() {
                const params = {}
                if (this.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                    params.fromPracticeId = this.practice.id
                } else {
                    params.fromUserId = this.user?.id
                }

                this.isFetchingData = true
                this.fetchReferralsCountByStatus(params, 'made')
            },

            calculateWindowWidth() {
                this.windowWidth = window.innerWidth
            },

            onReferralsCompleted(event) {
                if (event?.detail?.referralsCompleted && !this.$auth.isUserPracticeDemo()) {
                    this.fetchReceivedReferralsCountByStatus()
                    ReferralService.fetchPendingReferralActions()
                }
            },
        },

        mounted() {
            if (!this.$auth.isUserPracticeDemo() && !this.$auth.isUserTemporaryPhysician()) {
                this.fetchReceivedReferralsCountByStatus()
                this.fetchMadeReferralsCountByStatus()
                ReferralService.fetchPendingReferralActions()
            }
            window.addEventListener('referrals-complete-localstorage-changed', this.onReferralsCompleted)
            window.addEventListener('resize', this.calculateWindowWidth)
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.calculateWindowWidth)
            window.removeEventListener('referrals-complete-localstorage-changed', this.onReferralsCompleted)
        },
    }
</script>
